import { mergeStyles } from "@fluentui/react";
import { tokens } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";
import { getDownloadSignedUrl } from "api";
import { CitationsTooltip } from "components/CitationsTooltip";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { FileWithId } from "models/FileWithId";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { ICitation } from "models/ICitation";
import { IFile } from "models/IFile";
import { ITableColumn } from "models/ITableColumn";
import { IValidationRule } from "models/IValidationRule";
import { IWebSearchCitation } from "models/IWebSearchCitation";
import React from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { Constants, ConstantValues } from "../common/constants";
import { ICategory } from "../models/ICategory";
import { IGroup } from "../models/IGroup";
import { ITag } from "../models/ITag";
import { IAzureUser } from "../models/IAzureUser";
import { IUser } from "../models/IUser";

export const getTimeAndDate = (timestamp: number): string => {
  const dateObj = new Date(timestamp * 1000);

  const timeString = dateObj.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const dateString = dateObj.toDateString();

  return `${timeString} - ${dateString}`;
};

export const formatDate = (
  unixSeconds: number | undefined,
  fallback: string = "-",
): string => {
  if (!unixSeconds || isNaN(unixSeconds)) {
    return fallback;
  }

  return new Date(unixSeconds * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const convertDateStringToUnix = (
  dateString?: string,
): number | undefined => {
  if (!dateString) {
    return undefined;
  }

  return Math.floor(new Date(dateString).getTime() / 1000);
};

/**
 * @deprecated Use designTokens from theme/customTheme instead. Several values here are off-brand or lack token equivalents. Do not add new callers.
 */
export const NHColors = {
  gray: "#e5e7eb",
  primary: "#14243c",
  primaryOld: "#132e53",
  primary2: "#039fb8",
  primary2_60opacity: "#69C6D5",
  secondary: "#525252",
  secondary2: "#dee22a",
  white: "white",
};

export const isNullOrUndefined = (obj: any): boolean => {
  return obj === null || typeof obj === "undefined";
};

export const isNullUndefinedOrEmpty = (obj: any): boolean => {
  return isNullOrUndefined(obj) || obj === ConstantValues.EMPTY_STRING;
};

export const IsUndefined = (obj: any): boolean => {
  return typeof obj === "undefined";
};

type CellType = ITag | ICategory | IUser | IGroup | any;

export const renderCellValue = <T extends CellType>(
  item: T,
  columnKey: keyof T,
) => {
  const value = item[columnKey];

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return (
      <React.Fragment>
        {value?.map((item: T, index: number) => (
          <span key={index}> {JSON.stringify(item)} </span>
        ))}
      </React.Fragment>
    );
  }

  if (typeof value === "object" && value !== null) {
    return <span>{JSON.stringify(value)} </span>;
  }

  return null;
};

export const validateFields = <T,>(
  item: T,
  columns: ITableColumn<T>[],
  validationRules?: IValidationRule<T>[],
): string[] => {
  const messages: string[] = [];

  columns.forEach((column) => {
    const value = item[column.columnId as keyof T];

    if (
      column.required &&
      (value === undefined ||
        value === null ||
        value === ConstantValues.EMPTY_STRING)
    ) {
      messages.push(`${column.displayValue} is required.`);
    }

    if (validationRules) {
      validationRules.forEach((rule) => {
        if (rule.columnId === column.columnId) {
          const message = rule.validate(value);
          if (message) {
            messages.push(message);
          }
        }
      });
    }
  });

  return messages;
};

export const mapAzureUsersToModel = (
  azureUsers: Array<IUser> | null,
): IAzureUser[] => {
  if (!azureUsers) {
    return [];
  }

  return azureUsers.map((user, index) => {
    return {
      Uid: user.userPrincipalName ? user.userPrincipalName : `user-${index}`,
      Name: user.displayName || "Unknown",
      Email: user.mail || "No email provided",
    };
  });
};

export const mapAzureUserToModel = (
  user: IUser | null,
  index: number = 0,
): CustomOption => {
  if (!user) {
    return {
      key: ConstantValues.EMPTY_STRING,
      text: ConstantValues.EMPTY_STRING,
      secondaryText: ConstantValues.EMPTY_STRING,
    };
  }

  return {
    key: user.userPrincipalName ? user.userPrincipalName : `user-${index}`,
    text: user.displayName || "Unknown",
    secondaryText: user.mail || "No email provided",
    ...user,
  };
};

export const toUserOption = (
  userId: string,
  displayName?: string,
): CustomOption => ({
  key: userId,
  text: displayName || userId,
});

export const convertFileWithIdToIFile = (fileWithId: FileWithId): IFile => {
  return {
    filename: fileWithId.file.name,
    url: ConstantValues.EMPTY_STRING,
    id: fileWithId.id,
  };
};

export const getCitationKey = (citation: ICitation): string => {
  return `${citation.document}::${citation.text.trim()}`;
};

export const buildCitationKeyMap = (
  citations: ICitation[],
): {
  citationKeyMap: Map<string, number>;
  documentToCitationMap: Map<number, string>;
} => {
  const citationKeyMap = new Map<string, number>();
  const documentToCitationMap = new Map<number, string>();
  let counter = 1;

  const sorted = [...citations].sort(
    (a, b) => (a.answer_start_index ?? 0) - (b.answer_start_index ?? 0),
  );

  for (const citation of sorted) {
    if (
      citation.answer_start_index === undefined ||
      citation.answer_end_index === undefined
    )
      continue;

    const key = getCitationKey(citation);
    if (!citationKeyMap.has(key)) {
      citationKeyMap.set(key, counter);
      documentToCitationMap.set(counter, key);
      counter++;
    }
  }

  return { citationKeyMap, documentToCitationMap };
};

export const renderHighlightedWebCitations = (
  content: string,
  webCitations: IWebSearchCitation[],
): JSX.Element => {
  let modifiedContent = content;
  const uniqueTexts = new Set<string>();
  const citationMap = new Map<string, number>();
  let counter = 1;

  for (const citation of webCitations) {
    const text = citation.cited_text?.trim();
    if (text && !uniqueTexts.has(text) && modifiedContent.includes(text)) {
      const escapedText = text.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
      const regex = new RegExp(escapedText, "g");
      citationMap.set(text, counter);

      modifiedContent = modifiedContent.replace(
        regex,
        `<span class="${webCitationHighlightStyle}">${text}<sup>[${counter}]</sup></span>`,
      );

      uniqueTexts.add(text);
      counter++;
    }
  }

  return (
    <Markdown
      components={{
        h1: ({ node: _node, ...props }) => (
          <h1 style={{ lineHeight: "normal" }} {...props} />
        ),
      }}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
    >
      {modifiedContent}
    </Markdown>
  );
};

export const webCitationHighlightStyle: string = mergeStyles({
  backgroundColor: designTokens.semantic.infoLight,
  padding: "0 2px",
  borderRadius: "2px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
  ":hover": {
    backgroundColor: tokens.colorPaletteBlueBackground2,
  },
});

export const renderContentWithCitations = (
  content: string,
  citations: ICitation[],
) => {
  if (!citations || citations.length === 0) {
    return (
      <Markdown
        components={{
          h1: ({ node: _node, ...props }) => (
            <h1 style={{ lineHeight: "normal" }} {...props} />
          ),
        }}
        remarkPlugins={[remarkGfm]}
      >
        {content}
      </Markdown>
    );
  }

  const { citationKeyMap, documentToCitationMap: citationReverseMap } =
    buildCitationKeyMap(citations);

  let modifiedContent: string = ConstantValues.EMPTY_STRING;
  let lastIndex = 0;

  const sorted = [...citations].sort(
    (a, b) => (a.answer_start_index ?? 0) - (b.answer_start_index ?? 0),
  );

  const filteredSorted: ICitation[] = [];

  for (let idx = 0; idx < sorted?.length - 1; idx++) {
    if (
      sorted[idx].answer_start_index === undefined ||
      sorted[idx].answer_end_index === undefined
    )
      continue;
    if (
      (sorted[idx + 1]?.answer_start_index - 10 <
        sorted[idx]?.answer_start_index &&
        sorted[idx]?.answer_start_index <
          sorted[idx + 1]?.answer_start_index + 10) ||
      (sorted[idx + 1]?.answer_end_index - 10 < sorted[idx]?.answer_end_index &&
        sorted[idx]?.answer_end_index < sorted[idx + 1]?.answer_end_index + 10)
    ) {
      const key = getCitationKey(sorted[idx]);
      const key2 = getCitationKey(sorted[idx + 1]);
      if (key === key2) continue;
    }
    filteredSorted.push(sorted[idx]);
  }
  filteredSorted.push(sorted[sorted?.length - 1]);

  for (const citation of filteredSorted) {
    if (
      citation.answer_start_index === undefined ||
      citation.answer_end_index === undefined
    )
      continue;

    let endIndex = citation.answer_end_index;
    const sentenceEndRegex = /[\n\r\t]/;
    while (
      endIndex < content.length &&
      !sentenceEndRegex.test(content[endIndex])
    ) {
      endIndex++;
    }

    const key = getCitationKey(citation);
    const citationId = citationKeyMap.get(key);
    modifiedContent += content.slice(lastIndex, endIndex);
    if (citationId) {
      modifiedContent += ` [${citationId}](${citationId}) `;
    }

    lastIndex = endIndex;
  }

  modifiedContent += content.slice(lastIndex);

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ node: _node, ...props }) => (
          <h1 style={{ lineHeight: "normal" }} {...props} />
        ),
        a: ({ href, children }) => {
          const citationId = parseInt(href ?? "0");
          const key = citationReverseMap.get(citationId);
          const citation = citations.find((c) => getCitationKey(c) === key);

          const citationText = citation?.text
            .replace(/<\/filename>/gi, ConstantValues.EMPTY_STRING)
            .replace(
              /\S{1,200}\.\S{1,200}\s{0,50}/,
              ConstantValues.EMPTY_STRING,
            )
            .replace(
              citation?.file_name ?? ConstantValues.EMPTY_STRING,
              ConstantValues.EMPTY_STRING,
            )
            .trim();

          return citation && citationText && citationText.length > 0 ? (
            <CitationsTooltip
              citation={citation}
              documentNumber={citationId}
              citationText={citationText}
            />
          ) : (
            <span>[{children}]</span>
          );
        },
      }}
    >
      {modifiedContent}
    </Markdown>
  );
};

export const downloadFileFromCitation = async (citation: ICitation) => {
  try {
    const fileWithUrl = await getDownloadSignedUrl({
      filename:
        citation.url.includes("tags") ||
        citation.url.includes("workspaces") ||
        citation.url.includes("user_store")
          ? citation.url
          : citation.file_name,
    });
    if (fileWithUrl?.sas) {
      window.open(fileWithUrl.sas, "_blank");
    } else {
      console.error("Signed URL generation failed.");
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const userIsAdminOrPlatformAdmin = (user: any): boolean => {
  return (
    user.roles.includes(Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE) ||
    user.roles.includes(Constants.ADMINS_GROUP_NAME_VALUE)
  );
};

export const userIsDecoodaUser = (user: any): boolean => {
  return (
    user.roles.includes(Constants.DECOODA_USERS_GROUP_NAME_VALUE) ||
    user.roles.includes(Constants.DECOODA_ADMINS_GROUP_NAME_VALUE)
  );
};

export const userIsAiMetricsUser = (user: { roles?: string[] }): boolean => {
  return (
    (user?.roles ?? []).includes(Constants.AI_METRICS_USERS_GROUP_NAME_VALUE) ||
    (user?.roles ?? []).includes(Constants.AI_METRICS_ADMINS_GROUP_NAME_VALUE)
  );
};

export const userIsDiagnosticsUser = (user: any): boolean => {
  return user.roles.includes(Constants.DIAGNOSTICS_USERS_GROUP_NAME_VALUE);
};

export const userIsProjectIqUser = (user: { roles?: string[] }): boolean => {
  return (
    (user?.roles ?? []).includes(Constants.PROJECT_IQ_USERS_GROUP_NAME_VALUE) ||
    (user?.roles ?? []).includes(Constants.PROJECT_IQ_ADMINS_GROUP_NAME_VALUE)
  );
};

export const userIsProjectIqAdmin = (user: { roles?: string[] }): boolean => {
  return (user?.roles ?? []).includes(
    Constants.PROJECT_IQ_ADMINS_GROUP_NAME_VALUE,
  );
};

export const normalizeModelKey = (value?: string) =>
  (value ?? ConstantValues.EMPTY_STRING)
    .replace(/[\s-]/g, ConstantValues.EMPTY_STRING)
    .trim()
    .toLowerCase();

export const getModelByKey = (models: IAccessLLMModel[], key?: string) => {
  if (!key) {
    return undefined;
  }

  const modelKey = normalizeModelKey(key);
  return (
    models.find((m) => normalizeModelKey(m.name) === modelKey) ||
    models.find((m) => normalizeModelKey(m.id) === modelKey)
  );
};

export async function copyTextToClipboard(text: string): Promise<void> {
  if (!navigator?.clipboard?.writeText) {
    return;
  }

  await navigator.clipboard.writeText(text);
}

const removeBackgrounds = (element: HTMLElement): void => {
  element.style.backgroundColor = "transparent";
  element.style.background = "transparent";

  const allElements = element.getElementsByTagName("*");
  for (let i = 0; i < allElements.length; i++) {
    const el = allElements[i] as HTMLElement;
    el.style.backgroundColor = "transparent";
    el.style.background = "transparent";
  }
};

export async function copyRichText(
  sourceElement: HTMLElement,
  plainTextFallback: string,
): Promise<void> {
  const clonedElement = sourceElement.cloneNode(true) as HTMLElement;
  removeBackgrounds(clonedElement);

  if (navigator?.clipboard && typeof ClipboardItem !== "undefined") {
    try {
      const html = clonedElement.outerHTML;
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plainTextFallback], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      console.log("Failed to copy rich text, falling back to plain text.");
    }
  }

  const tempContainer = document.createElement("div");
  tempContainer.style.position = "absolute";
  tempContainer.style.left = "-9999px";
  document.body.appendChild(tempContainer);
  tempContainer.appendChild(clonedElement);

  const selection = window.getSelection();
  const range = document.createRange();

  selection?.removeAllRanges();
  range.selectNodeContents(tempContainer);
  selection?.addRange(range);

  try {
    document.execCommand("copy");
  } catch {
    await copyTextToClipboard(plainTextFallback);
  } finally {
    selection?.removeAllRanges();
    document.body.removeChild(tempContainer);
  }
}

export const isValidGuid = (value?: string | null): boolean => {
  if (!value) {
    return false;
  }

  return (
    /^[0-9a-f]{24}$/i.test(value) ||
    /^[0-9a-f]{32}$/i.test(value) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value,
    )
  );
};

export const nowUnix = () => Math.floor(Date.now() / 1000);
export const generateId = () => crypto.randomUUID();

export const stripMarkdown = (text: string): string => {
  return text
    .replace(/```[\s\S]*?```/g, (match) =>
      match.replace(/```\w*\n?/g, "").trim(),
    )
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\[([^\]]{1,500})\]\([^)]{1,2000}\)/g, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/^[-*+]\s+/gm, "• ")
    .replace(/\n{3,}/g, "\n\n");
};

export const stripHtmlTags = (input: string | undefined): string => {
  if (!input) {
    return ConstantValues.EMPTY_STRING;
  }
  return (
    new DOMParser().parseFromString(input, "text/html").body.textContent ??
    ConstantValues.EMPTY_STRING
  );
};

export const isRichTextEmpty = (content: string | undefined): boolean => {
  if (!content) return true;
  const doc = new DOMParser().parseFromString(content, "text/html");
  // eslint-disable-next-line no-irregular-whitespace
  return (doc.body.textContent ?? "").replace(/ /g, "").trim().length === 0;
};

const pluralize = (n: number, unit: string): string =>
  `${n} ${unit}${n === 1 ? "" : "s"} ago`;

export const getRelativeTime = (unixTimestamp: number): string => {
  const now = Date.now();
  const timestamp = unixTimestamp * 1000;
  const diffInSeconds = Math.floor((now - timestamp) / 1000);

  if (diffInSeconds < 60) return "Just now";

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return pluralize(diffInMinutes, "minute");

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return pluralize(diffInHours, "hour");

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return pluralize(diffInDays, "day");

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return pluralize(diffInMonths, "month");

  const diffInYears = Math.floor(diffInMonths / 12);
  return pluralize(diffInYears, "year");
};

export const escapeHtml = (text: string): string => {
  const htmlEscapeMap: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };
  return text.replace(/[&<>"'/]/g, (char) => htmlEscapeMap[char]);
};

export const sanitizeTemplateContent = (
  content: string | undefined,
): string => {
  if (!content) return ConstantValues.EMPTY_STRING;
  return escapeHtml(content.trim());
};

export type SortingOptions = { field: string; order: "asc" | "desc" };

export const readSortingOptions = <T = SortingOptions,>(
  scope: string,
): T | null => {
  try {
    const raw = localStorage.getItem(ConstantValues.SORTING_OPTIONS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.[scope] ?? null;
  } catch {
    return null;
  }
};

export const writeSortingOptions = (
  scope: string,
  value: Record<string, unknown>,
): void => {
  try {
    const raw = localStorage.getItem(ConstantValues.SORTING_OPTIONS_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[scope] = value;
    localStorage.setItem(
      ConstantValues.SORTING_OPTIONS_CACHE_KEY,
      JSON.stringify(parsed),
    );
  } catch (e) {
    console.error("Error writing sorting options:", e);
  }
};

export const escapeCSVField = (value: string): string =>
  `"${(value || "").replace(/"/g, '""')}"`;

export const generateCSV = <T,>(
  items: T[],
  columns: { header: string; accessor: (item: T) => string }[],
): string => {
  const headers = columns.map((col) => col.header);
  const rows = items.map((item) =>
    columns.map((col) => escapeCSVField(col.accessor(item))).join(","),
  );
  return [headers.join(","), ...rows].join("\n");
};

export const downloadCSV = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

const NULL_TIMESTAMP_DISPLAY = "—";

const LOCAL_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

export const formatLocalDateTime = (iso: string | null | undefined): string => {
  if (!iso) return NULL_TIMESTAMP_DISPLAY;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NULL_TIMESTAMP_DISPLAY;
  return parsed.toLocaleString(undefined, LOCAL_DATE_TIME_OPTIONS);
};
